import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Switch, Popup, Toast } from "antd-mobile";
import { useUserStore } from "@/store/useUserStore";
import { fetchUserProfile, type UserProfile } from "@/api/user";

export default function SettingsPage() {
  const navigate = useNavigate();
  const userInfo = useUserStore((state) => state.userInfo);

  // 用户详情数据（从API获取，与 profile 页面一致）
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const phone = profile?.phone || userInfo?.phone;
  const [notifyEnabled, setNotifyEnabled] = useState(true);

  // 加载用户详细信息（与 profile 页面一致）
  useEffect(() => {
    fetchUserProfile()
      .then((data) => setProfile(data.user))
      .catch((err) => console.error("获取用户信息失败:", err));
  }, []);
  const [showPhoneDrawer, setShowPhoneDrawer] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [code, setCode] = useState("");
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 获取验证码倒计时
  const handleGetCode = () => {
    if (!newPhone.trim()) {
      Toast.show({ icon: "fail", content: "请输入新手机号" });
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(newPhone)) {
      Toast.show({ icon: "fail", content: "手机号格式不正确" });
      return;
    }
    setCountdown(60);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    Toast.show({ icon: "success", content: "验证码已发送" });
  };

  // 确认修改
  const handleSubmitPhone = () => {
    if (!newPhone.trim()) { Toast.show({ icon: "fail", content: "请输入新手机号" }); return; }
    if (!/^1[3-9]\d{9}$/.test(newPhone)) { Toast.show({ icon: "fail", content: "手机号格式不正确" }); return; }
    if (!code.trim()) { Toast.show({ icon: "fail", content: "请输入验证码" }); return; }
    // TODO: 对接修改手机号接口
    Toast.show({ icon: "success", content: "手机号修改成功" });
    setShowPhoneDrawer(false);
    setNewPhone("");
    setCode("");
  };

  return (
    <div className="min-h-screen" style={{ background: "#F5F5F5", fontFamily: "'PingFang SC','SF Pro Text','Inter',system-ui,sans-serif" }}>
      {/* ── 顶部导航栏 ── */}
      <div className="sticky top-0 z-10 flex items-center px-4 bg-white border-b border-gray-100 pb-3"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
        <button onClick={() => navigate("/profile")} className="flex items-center justify-center cursor-pointer active:bg-gray-100 transition-colors mr-2.5">
          <i className="ri-arrow-left-s-line text-gray-800" style={{ fontSize: 22 }} />
        </button>
        <span className="text-base font-semibold text-gray-900">设置</span>
      </div>

      {/* ── 账号与安全 ── */}
      <div className="px-4 pt-3">
        <span className="text-xs font-medium text-gray-400">账号与安全</span>
        <div className="bg-white rounded-xl mt-1.5 overflow-hidden shadow-sm">
          <button
            className="w-full flex items-center gap-3 px-4 py-3 cursor-pointer active:bg-gray-50 transition-colors"
            onClick={() => setShowPhoneDrawer(true)}
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: "#E6FAF0" }}>
              <i className="ri-smartphone-line" style={{ color: "#10B981", fontSize: 17 }} />
            </div>
            <span className="flex-1 text-left text-sm text-gray-800">手机号修改</span>
            <span className="text-xs text-gray-400 mr-1.5">{phone}</span>
            <i className="ri-arrow-right-s-line text-gray-300" style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>

      {/* ── 消息设置 ── */}
      <div className="px-4 pt-3">
        <span className="text-xs font-medium text-gray-400">消息设置</span>
        <div className="bg-white rounded-xl mt-1.5 overflow-hidden shadow-sm">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: "#FFF7E6" }}>
              <i className="ri-notification-3-line" style={{ color: "#F59E0B", fontSize: 17 }} />
            </div>
            <span className="flex-1 text-left text-sm text-gray-800">消息通知</span>
            <Switch checked={notifyEnabled} onChange={(v) => setNotifyEnabled(v)} style={{ "--height": "22px", "--width": "40px", "--checked-color": "#FF6B35" } as React.CSSProperties} />
          </div>
        </div>
      </div>

      {/* ── 法律条款 ── */}
      <div className="px-4 pt-3 pb-8">
        <span className="text-xs font-medium text-gray-400">法律条款</span>
        <div className="bg-white rounded-xl mt-1.5 overflow-hidden shadow-sm">
          <button
            className="w-full flex items-center gap-3 px-4 py-3 cursor-pointer active:bg-gray-50 transition-colors"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: "#F5F3FF" }}>
              <i className="ri-file-text-line" style={{ color: "#7C3AED", fontSize: 17 }} />
            </div>
            <span className="flex-1 text-left text-sm text-gray-800">用户协议</span>
            <i className="ri-arrow-right-s-line text-gray-300" style={{ fontSize: 16 }} />
          </button>
          <button
            className="w-full flex items-center gap-3 px-4 py-3 cursor-pointer active:bg-gray-50 transition-colors"
            style={{ borderTop: "1px solid #F5F5F5" }}
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: "#ECFDF5" }}>
              <i className="ri-shield-check-line" style={{ color: "#059669", fontSize: 17 }} />
            </div>
            <span className="flex-1 text-left text-sm text-gray-800">隐私政策</span>
            <i className="ri-arrow-right-s-line text-gray-300" style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>

      {/* ── 手机号修改抽屉弹窗 ── */}
      <Popup
        visible={showPhoneDrawer}
        onMaskClick={() => setShowPhoneDrawer(false)}
        position="bottom"
        round
        bodyStyle={{
          padding: "20px 20px 32px",
          maxHeight: "80vh",
          overflowY: "auto",
          borderTopLeftRadius: 30,
          borderTopRightRadius: 30,
        }}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between mb-5">
          <span className="text-base font-bold text-gray-900">手机号修改</span>
          <button
            onClick={() => setShowPhoneDrawer(false)}
            className="w-7 h-7 flex items-center justify-center rounded-full cursor-pointer hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-gray-400" style={{ fontSize: 18 }} />
          </button>
        </div>

        {/* 当前手机号 */}
        <div className="text-xs text-gray-500 mb-4">
          当前手机号：<span className="text-gray-700 font-medium">{phone}</span>
        </div>

        {/* 表单 */}
        <div className="space-y-3.5 mb-5">
          {/* 新手机号 */}
          <div>
            <label className="text-sm text-gray-600 mb-1.5 block">新手机号：</label>
            <input
              type="tel"
              className="w-full px-3 py-2.5 rounded-lg bg-gray-50 outline-none text-sm text-gray-800 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-200 transition-all"
              placeholder="请输入新手机号"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              maxLength={11}
            />
          </div>
          {/* 验证码 */}
          <div>
            <label className="text-sm text-gray-600 mb-1.5 block">验证码</label>
            <div className="flex gap-2.5">
              <input
                className="flex-1 px-3 py-2.5 rounded-lg bg-gray-50 outline-none text-sm text-gray-800 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-200 transition-all"
                placeholder="请输入验证码"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={6}
              />
              <button
                onClick={handleGetCode}
                disabled={countdown > 0}
                className="px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: countdown > 0 ? "#F0F0F0" : "#FFF2EB",
                  color: countdown > 0 ? "#999" : "#FF6B35",
                  border: "none",
                }}
              >
                {countdown > 0 ? `${countdown}s` : "获取验证码"}
              </button>
            </div>
          </div>
        </div>

        {/* 提交按钮 */}
        <button
          onClick={handleSubmitPhone}
          className="w-full py-3 rounded-full text-sm font-bold text-white cursor-pointer active:scale-[0.98] transition-all"
          style={{ background: "#FF6B35" }}
        >
          确认修改
        </button>
      </Popup>
    </div>
  );
}
